/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * HtmlCssEditor — Monaco Editor with HTML/CSS/JS tabs.
 * Validates code on Save/Run. Renders preview in a sandboxed iframe.
 */

import React, { useState, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import EditorField from './_EditorField';
import { useTheme } from '../../../theme/ThemeContext';
import HtmlCssDataBindingsPanel from '../HtmlCssDataBindingsPanel';
import HtmlCssLinkNavPanel from '../HtmlCssLinkNavPanel';

const TABS = [
  { key: 'html',     label: 'HTML',          language: 'html',       icon: 'bi-filetype-html' },
  { key: 'css',      label: 'CSS',           language: 'css',        icon: 'bi-filetype-css' },
  { key: 'js',       label: 'JS',            language: 'javascript', icon: 'bi-filetype-js' },
  { key: 'bindings', label: 'Data Bindings', icon: 'bi-database' },
  { key: 'links',    label: 'Links',         icon: 'bi-signpost-2' },
];

function validateHTML(html) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const errors = doc.querySelectorAll('parsererror');
    if (errors.length > 0) return 'HTML parse error detected.';
    return null;
  } catch (_) {
    return 'HTML validation failed.';
  }
}

function validateCSS(css) {
  if (!css.trim()) return null;
  try {
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
    document.head.removeChild(style);
    return null;
  } catch (_) {
    return 'CSS validation failed.';
  }
}

export default function HtmlCssEditor({ section, dispatch, appId }) {
  const { theme } = useTheme();
  const cfg = section.section_config || {};
  const [activeTab, setActiveTab] = useState('html');
  const [validationErrors, setValidationErrors] = useState({});
  const [previewKey, setPreviewKey] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  const update = patch => dispatch({
    type: 'UPDATE_SECTION_CONFIG',
    clientId: section._clientId,
    patch,
  });

  const handleChange = (value) => {
    update({ [activeTab]: value || '' });
  };

  const runValidation = useCallback(() => {
    const errors = {};
    const htmlErr = validateHTML(cfg.html || '');
    const cssErr = validateCSS(cfg.css || '');
    if (htmlErr) errors.html = htmlErr;
    if (cssErr) errors.css = cssErr;
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [cfg.html, cfg.css]);

  const handleRunPreview = () => {
    runValidation();
    setPreviewKey(k => k + 1);
    setShowPreview(true);
  };

  const iframeSrc = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          :root {
            --primary-color: ${theme.primary};
            --secondary-color: ${theme.secondary};
            --gradient-bg: ${theme.gradientBackground};
          }
          body { margin: 0; font-family: sans-serif; }
          ${cfg.css || ''}
        </style>
      </head>
      <body>
        ${cfg.html || ''}
        <script>${cfg.js || ''}<\/script>
      </body>
    </html>
  `;

  const currentValue = cfg[activeTab] || '';
  const currentLang = TABS.find(t => t.key === activeTab)?.language || 'html';

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: 8 }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '6px 14px',
              border: 'none',
              borderBottom: activeTab === tab.key ? `2px solid ${theme.primary}` : '2px solid transparent',
              background: 'transparent',
              color: activeTab === tab.key ? theme.primary : '#64748b',
              fontWeight: activeTab === tab.key ? 700 : 400,
              fontSize: 12,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <i className={`bi ${tab.icon}`} />
            {tab.label}
            {validationErrors[tab.key] && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#dc2626', display: 'inline-block' }} />
            )}
          </button>
        ))}
      </div>

      {/* Monaco Editor / config panels */}
      {activeTab === 'bindings' ? (
        <HtmlCssDataBindingsPanel
          appId={appId}
          bindings={section.data_binding?.bindings || []}
          onChange={(bindings) => dispatch({ type: 'UPDATE_DATA_BINDING', clientId: section._clientId, patch: { bindings } })}
        />
      ) : activeTab === 'links' ? (
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          <HtmlCssLinkNavPanel
            appId={appId}
            html={cfg.html || ''}
            navigations={cfg.link_navigations || []}
            onChange={(link_navigations) => update({ link_navigations })}
          />
        </div>
      ) : (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', height: 220 }}>
          <Editor
            height="220px"
            language={currentLang}
            value={currentValue}
            onChange={handleChange}
            options={{
              minimap: { enabled: false },
              fontSize: 12,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              automaticLayout: true,
            }}
            theme="vs"
          />
        </div>
      )}

      {/* Validation status */}
      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        {['html', 'css'].map(key => (
          <span
            key={key}
            style={{
              fontSize: 11,
              color: validationErrors[key] ? '#dc2626' : '#16a34a',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <i className={`bi ${validationErrors[key] ? 'bi-x-circle' : 'bi-check-circle'}`} />
            {key.toUpperCase()} {validationErrors[key] ? 'Error' : 'OK'}
          </span>
        ))}
      </div>

      {/* Render mode — must match the client's HtmlCssSection render_mode */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, marginTop: 10, padding: '8px 10px',
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>Full page</div>
          <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.35 }}>
            Turn on for a complete pasted website. Required for 100vh heroes, fixed
            navbars and #anchor links — inline mode auto-sizes to content, which makes
            viewport units grow without bound.
          </div>
        </div>
        <button
          type="button"
          onClick={() => update({ render_mode: cfg.render_mode === 'fullpage' ? 'inline' : 'fullpage' })}
          style={{
            flexShrink: 0,
            width: 38, height: 21, borderRadius: 999, border: 'none', cursor: 'pointer',
            background: cfg.render_mode === 'fullpage' ? theme.primary : '#cbd5e1',
            position: 'relative', transition: 'background 0.15s',
          }}
          aria-pressed={cfg.render_mode === 'fullpage'}
          aria-label="Full page render mode"
        >
          <span style={{
            position: 'absolute', top: 2, left: cfg.render_mode === 'fullpage' ? 19 : 2,
            width: 17, height: 17, borderRadius: '50%', background: '#fff',
            transition: 'left 0.15s',
          }} />
        </button>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          type="button"
          className="btn btn-sm"
          style={{ background: theme.primary, color: '#fff', border: 'none', fontSize: 12 }}
          onClick={handleRunPreview}
        >
          <i className="bi bi-play-fill me-1" />
          Run Preview
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          style={{ fontSize: 12 }}
          onClick={() => { runValidation(); }}
        >
          <i className="bi bi-check2-circle me-1" />
          Validate
        </button>
      </div>

      {/* Inline Preview */}
      {showPreview && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
            LIVE PREVIEW
            <button
              type="button"
              className="btn btn-sm btn-link p-0 ms-2"
              style={{ fontSize: 11 }}
              onClick={() => setShowPreview(false)}
            >
              Hide
            </button>
          </div>
          <iframe
            key={previewKey}
            srcDoc={iframeSrc}
            sandbox="allow-scripts allow-forms"
            style={{
              width: '100%',
              height: 200,
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              background: '#fff',
            }}
            title="HTML/CSS/JS Preview"
          />
        </div>
      )}
    </div>
  );
}
